import { createElement, forwardRef, useEffect, useState, type HTMLAttributes } from 'react';
import { Wrench } from 'lucide-react';
import type { JsonObject } from '@inf-monkeys-tech/monkeys';
import { useMonkeysAssetIconResolver, useMonkeysAssetImageResolver, useMonkeysComponentAttributes } from '../../provider/context';
import { safeDisplayUrl } from '../safeDisplayUrl';
import { cn } from './utils';

export type BaseAssetIconDescriptor =
  | { kind: 'image'; src: string }
  | { kind: 'emoji'; value: string; backgroundColor?: string }
  | { kind: 'lucide'; name: string };
export interface BaseAssetIconProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  icon: BaseAssetIconDescriptor;
  alt: string;
  normalizeImageIcon?: boolean;
}
export const BASE_ASSET_ICON_DESCRIPTOR_SCHEMA: JsonObject = {
  oneOf: [
    { type: 'object', additionalProperties: false, required: ['kind', 'src'], properties: { kind: { const: 'image' }, src: { type: 'string', minLength: 1, maxLength: 4096 } } },
    { type: 'object', additionalProperties: false, required: ['kind', 'value'], properties: { kind: { const: 'emoji' }, value: { type: 'string', minLength: 1, maxLength: 64 }, backgroundColor: { type: 'string', maxLength: 128 } } },
    { type: 'object', additionalProperties: false, required: ['kind', 'name'], properties: { kind: { const: 'lucide' }, name: { type: 'string', minLength: 1, maxLength: 128 } } },
  ],
};

export type AssetIconTag = 'path' | 'circle' | 'rect' | 'polyline' | 'line' | 'ellipse' | 'polygon';
export type AssetIconNode = readonly [tag: AssetIconTag, attributes: Readonly<Record<string, string>>];
/** Host data remains untrusted until the component validates every node. */
export type AssetIconResolver = (name: string) => Promise<unknown>;
export type AssetImageResolver = (reference: string, signal: AbortSignal) => Promise<string | undefined>;
const attributesByTag: Record<AssetIconTag, readonly string[]> = {
  path: ['d'], circle: ['cx', 'cy', 'r'], rect: ['x', 'y', 'width', 'height', 'rx', 'ry'],
  polyline: ['points'], line: ['x1', 'x2', 'y1', 'y2'], ellipse: ['cx', 'cy', 'rx', 'ry'], polygon: ['points'],
};
export function parseAssetIconNodes(value: unknown): readonly AssetIconNode[] | undefined {
  if (!Array.isArray(value) || !value.length || value.length > 256) return undefined;
  const result: AssetIconNode[] = [];
  for (const node of value) {
    if (!Array.isArray(node) || node.length !== 2 || typeof node[0] !== 'string' || !Object.prototype.hasOwnProperty.call(attributesByTag, node[0])) return undefined;
    const tag = node[0] as AssetIconTag;
    const raw = node[1];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw) || Object.getPrototypeOf(raw) !== Object.prototype) return undefined;
    const attributes: Record<string, string> = {};
    for (const [key, descriptor] of Object.entries(Object.getOwnPropertyDescriptors(raw))) {
      if (!('value' in descriptor) || typeof descriptor.value !== 'string') return undefined;
      const text = descriptor.value;
      if (key === 'key') { if (text.length > 256) return undefined; }
      else if (key === 'fill') { if (text !== 'currentColor' && text !== 'none') return undefined; }
      else if (!attributesByTag[tag].includes(key)) return undefined;
      else if (key === 'd') { if (!text.length || text.length > 8192 || !/^[MmZzLlHhVvCcSsQqTtAa0-9eE+.,\s-]+$/.test(text)) return undefined; }
      else if (key === 'points') { if (!text.length || text.length > 8192 || !/^[0-9eE+.,\s-]+$/.test(text)) return undefined; }
      else if (!text.trim() || text.length > 128 || !Number.isFinite(Number(text))) return undefined;
      attributes[key] = text;
    }
    result.push([tag, attributes]);
  }
  return result;
}
export const BaseAssetIcon = forwardRef<HTMLDivElement, BaseAssetIconProps>(function BaseAssetIcon(
  { icon, alt, normalizeImageIcon = false, className, style, ...props }, ref,
) {
  const attributes = useMonkeysComponentAttributes();
  const resolver = useMonkeysAssetIconResolver();
  const imageResolver = useMonkeysAssetImageResolver();
  const imageReference = icon.kind === 'image' ? icon.src : undefined;
  const [image, setImage] = useState<{ reference: string; resolver: AssetImageResolver; src?: string }>();
  useEffect(() => {
    if (!imageReference || !imageResolver) return;
    const controller = new AbortController();
    void Promise.resolve().then(() => imageResolver(imageReference, controller.signal)).then(src => {
      if (!controller.signal.aborted) setImage({ reference: imageReference, resolver: imageResolver, src: safeDisplayUrl(src, 'media') });
    }).catch(() => { if (!controller.signal.aborted) setImage({ reference: imageReference, resolver: imageResolver }); });
    return () => controller.abort();
  }, [imageReference, imageResolver]);
  const imageSource = imageResolver
    ? image && image.reference === imageReference && image.resolver === imageResolver ? image.src : undefined
    : safeDisplayUrl(imageReference, 'media');
  const name = icon.kind === 'lucide' ? icon.name : undefined;
  const [loaded, setLoaded] = useState<{ name: string; resolver: AssetIconResolver; nodes: readonly AssetIconNode[] | undefined }>();
  useEffect(() => {
    let active = true;
    if (name && resolver) void Promise.resolve().then(() => resolver(name)).then(value => { if (active) setLoaded({ name, resolver, nodes: parseAssetIconNodes(value) }); }).catch(() => { if (active) setLoaded({ name, resolver, nodes: undefined }); });
    return () => { active = false; };
  }, [name, resolver]);
  const nodes = loaded?.name === name && loaded?.resolver === resolver ? loaded?.nodes : undefined;
  return <div {...attributes} {...props} ref={ref} role="img" aria-label={alt}
    className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', icon.kind === 'emoji' ? 'text-2xl' : icon.kind === 'image' && !normalizeImageIcon ? 'overflow-hidden border' : 'bg-muted', className)}
    style={{ ...(icon.kind === 'emoji' && icon.backgroundColor ? { backgroundColor: icon.backgroundColor } : {}), ...style }}>
    {icon.kind === 'image' ? imageSource ? <img src={imageSource} alt="" aria-hidden="true" loading="lazy" className={normalizeImageIcon ? 'h-5 w-5 object-contain' : 'h-full w-full object-cover'} /> : <Wrench aria-hidden="true" className="h-5 w-5 text-muted-foreground" />
      : icon.kind === 'emoji' ? <span aria-hidden="true">{icon.value}</span>
        : nodes ? <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-muted-foreground">{nodes.map(([tag, attributes], index) => { const { key, ...props } = attributes; return createElement(tag, { ...props, key: key ?? index }); })}</svg>
          : <Wrench aria-hidden="true" className="h-5 w-5 text-muted-foreground" />}
  </div>;
});
